import React from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Avatar,
  Tooltip,
  SxProps,
  Theme,
} from "@mui/material";
import {
  RotateLeft as RotateLeftIcon,
  ExitToApp as ExitToAppIcon,
} from "@mui/icons-material";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import UserIcon from "../../assets/images/user.svg";
import loginLeafMark from "../../assets/images/loginLeafMark.png";
import { useNavigate, useLocation } from "react-router-dom";
import { LoginRootState } from "../../types/login";
import CloseIcon from "@mui/icons-material/Close";
import { useAppSelector, useAppDispatch } from "../../globalState/hooks";
import loginService from "../../services/LoginServices";
import { showToast } from "../component/common/toastNotification";
import { useTranslation } from "react-i18next";
import { setUserLogoutData } from "../../globalState/actions/authAction/authAction";
import { getLoginCompanyName } from "../../helper/normalizeLoginResponse";
import { NJIM } from "../../theme/njimTheme";

interface SidebarProps {
  onClose?: () => void;
}

const getIconStyles = (isActive: boolean): SxProps<Theme> => ({
  color: isActive ? NJIM.ink : NJIM.textSecondary,
  fontSize: 20,
});

const getNavItemStyles = (
  isActive: boolean
): SxProps<Theme> => ({
  cursor: "pointer",
  position: "relative",
  backgroundColor: isActive ? NJIM.mintWash : "transparent",
  borderRadius: "12px",
  margin: "4px 12px",
  paddingBlock: "10px",
  paddingInline: "14px",
  transition: "background-color 180ms ease, color 180ms ease",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  "&::before": {
    content: '""',
    position: "absolute",
    insetInlineStart: 0,
    top: 8,
    bottom: 8,
    width: 3,
    borderRadius: 999,
    background: isActive ? NJIM.mint : "transparent",
  },
  "& .MuiListItemIcon-root": {
    minWidth: "28px",
    justifyContent: "center",
  },
  "& .MuiListItemText-root": {
    margin: 0,
    flex: 1,
    minWidth: 0,
  },
  "& .MuiListItemText-primary": {
    textAlign: "start",
    whiteSpace: "nowrap",
    fontSize: "14px",
    fontWeight: isActive ? 600 : 400,
    color: isActive ? NJIM.ink : NJIM.textSecondary,
  },
  "&:hover": {
    backgroundColor: isActive ? NJIM.mintWash : NJIM.muted,
  },
});

const sectionLabelSx: SxProps<Theme> = {
  mx: 2,
  mt: 2.5,
  mb: 1.5,
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: NJIM.textSecondary,
};

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector(
    (state: LoginRootState) => state.authReducer.loginDetials
  );
  const role = userDetails?.role;
  const sidebarDisplayName = (() => {
    const fullName = `${userDetails?.firstName ?? ""} ${userDetails?.lastName ?? ""}`.trim();
    if (fullName.toLowerCase() === "portal admin") {
      return t("sidebar.portalAdmin");
    }
    return fullName;
  })();
  // companyName from login (Sage X3) — same as mobile app, no frontend override
  const sidebarTitle =
    role === "User"
      ? getLoginCompanyName(userDetails)
      : t("sidebar.alHayat");
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    localStorage.setItem("logging_out", "true");
    dispatch(setUserLogoutData());
    localStorage.removeItem("token");

    try {
      await loginService.logout();
    } catch {
      // Best-effort; client-side session is already cleared
    }

    localStorage.clear();
    try {
      sessionStorage.removeItem("cod.servedCustomer");
    } catch {
      // ignore
    }
    navigate("/", { replace: true });
    showToast(t("sidebar.logoutSuccessful"), "success");
  };

  // Navigation configuration for user role-----------------
  const userNavItems = [
    {
      path: "/dashboard",
      icon: DashboardOutlinedIcon,
      translationKey: "sidebar.dashboard",
    },
    {
      path: "/saleOrder",
      icon: ShoppingCartOutlinedIcon,
      translationKey: "sidebar.salesOrders",
    },
    {
      path: "/invoicesPayments",
      icon: DescriptionOutlinedIcon,
      translationKey: "sidebar.invoicesPayments",
    },
    {
      path: "/walletBalance",
      icon: AccountBalanceWalletOutlinedIcon,
      translationKey: "sidebar.walletBalance",
    },
    {
      path: "/deliveryManagement",
      icon: LocalShippingOutlinedIcon,
      translationKey: "sidebar.deliveryManagement",
    },
    {
      path: "/returnsManagement",
      icon: RotateLeftIcon,
      translationKey: "sidebar.returns",
    },
  ];
  // Navigation configuration for Admin role-----------------
  const adminNavItems = [
    {
      path: "/CustomerEnrolment",
      icon: ManageAccountsOutlinedIcon,
      translationKey: "sidebar.customerEnrolment",
    },
    {
      path: "/SalesRepEnrolment",
      icon: PointOfSaleOutlinedIcon,
      translationKey: "sidebar.salesRepEnrolment",
    },
    {
      path: "/SalesManagerEnrolment",
      icon: FactCheckOutlinedIcon,
      translationKey: "sidebar.salesManagerEnrolment",
    },
  ];
  // Navigation configuration for Supervisor role-----------------
  const supervisorNavItems = [
    {
      path: "/workflowPortal",
      icon: AccountTreeOutlinedIcon,
      translationKey: "sidebar.workflowPortal",
    },
    {
      path: "/supervisorReports",
      icon: AssessmentOutlinedIcon,
      translationKey: "sidebar.opsReports",
    },
  ];
  // Navigation configuration for WHM role-----------------
  const whmNavItems = [
    {
      path: "/itemControlRelease",
      icon: InventoryOutlinedIcon,
      translationKey: "sidebar.itemControlRelease",
    },
    {
      path: "/inventoryReconciliation",
      icon: BalanceOutlinedIcon,
      translationKey: "sidebar.inventoryReconciliation",
    },
    {
      path: "/whmReports",
      icon: AssessmentOutlinedIcon,
      translationKey: "sidebar.opsReports",
    },
  ];
  const salesRepNavItems = [
    {
      path: "/codSalesRep",
      icon: PointOfSaleOutlinedIcon,
      translationKey: "sidebar.codSalesRep",
    },
    {
      path: "/codSalesRepReports",
      icon: AssessmentOutlinedIcon,
      translationKey: "sidebar.codReports",
    },
  ];
  const salesManagerNavItems = [
    {
      path: "/codSalesManager",
      icon: FactCheckOutlinedIcon,
      translationKey: "sidebar.codSalesManager",
    },
    {
      path: "/codSalesManagerReports",
      icon: AssessmentOutlinedIcon,
      translationKey: "sidebar.codReports",
    },
  ];
  return (
    <Box sx={{ display: "flex" }} className="sidebar_sec">
      <Drawer
        sx={{
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            position: "relative",
            background: NJIM.white,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            color: NJIM.textPrimary,
            paddingTop: "12px",
            width: { xs: "82vw", sm: "268px" },
            minWidth: { xs: "82vw", sm: "268px" },
            maxWidth: { xs: "82vw", sm: "268px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            boxShadow: "none",
            borderInlineEnd: `1px solid ${NJIM.border}`,
            resize: "none",
            overflow: "hidden auto",
            direction: isRTL ? "rtl" : "ltr",
            minHeight: "100vh",
          },
        }}
        variant="permanent"
        anchor={isRTL ? "right" : "left"}
      >
        <Box sx={{ position: "relative", zIndex: 1, flex: 1, minHeight: 0 }}>
          <Box className="sidebar-close-btn" onClick={onClose}>
            <CloseIcon sx={{ color: NJIM.textPrimary, fontSize: 26 }} />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              height: 64,
              paddingX: 2,
              cursor: "pointer",
              borderBottom: `1px solid ${NJIM.border}`,
            }}
            onClick={() => {
              if (role === "Supervisor") navigate("/workflowPortal");
              else if (role === "Admin") navigate("/CustomerEnrolment");
              else if (role === "WHM") navigate("/itemControlRelease");
              else if (role === "SalesRep") navigate("/codSalesRep");
              else if (role === "SalesManager") navigate("/codSalesManager");
              else navigate("/dashboard");
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                bgcolor: NJIM.ink,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={loginLeafMark}
                alt=""
                sx={{ width: 20, height: 20, objectFit: "contain" }}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: NJIM.textPrimary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 170,
                  lineHeight: 1.3,
                }}
                title={sidebarTitle}
              >
                {sidebarTitle}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: NJIM.textSecondary,
                  lineHeight: 1.3,
                }}
              >
                {t("sidebar.pharmaceuticalPortal")}
              </Typography>
            </Box>
          </Box>

          <List sx={{ mt: 2, px: 0, pb: 2 }}>
            {role === "User" && (
              <>
                <Typography sx={{ ...sectionLabelSx, mt: 0 }}>
                  {t("sidebar.customerPortalB2B")}
                </Typography>

                {userNavItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <ListItem
                      key={item.path}
                      sx={getNavItemStyles(isActive)}
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemIcon>
                        <IconComponent sx={getIconStyles(isActive)} />
                      </ListItemIcon>
                      <ListItemText primary={t(item.translationKey)} />
                    </ListItem>
                  );
                })}
              </>
            )}

            {role === "Admin" && (
              <>
                <Typography sx={sectionLabelSx}>
                  {t("sidebar.internalPortal")}
                </Typography>

                {adminNavItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <ListItem
                      key={item.path}
                      sx={getNavItemStyles(isActive)}
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemIcon>
                        <IconComponent sx={getIconStyles(isActive)} />
                      </ListItemIcon>
                      <ListItemText primary={t(item.translationKey)} />
                    </ListItem>
                  );
                })}
              </>
            )}

            {role === "Supervisor" && (
              <>
                <Typography sx={sectionLabelSx}>
                  {t("sidebar.supervisorPortal")}
                </Typography>

                {supervisorNavItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <ListItem
                      key={item.path}
                      sx={getNavItemStyles(isActive)}
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemIcon>
                        <IconComponent sx={getIconStyles(isActive)} />
                      </ListItemIcon>
                      <ListItemText primary={t(item.translationKey)} />
                    </ListItem>
                  );
                })}
              </>
            )}

            {role === "WHM" && (
              <>
                <Typography sx={sectionLabelSx}>
                  {t("sidebar.whmPortalSection")}
                </Typography>

                {whmNavItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <ListItem
                      key={item.path}
                      sx={getNavItemStyles(isActive)}
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemIcon>
                        <IconComponent sx={getIconStyles(isActive)} />
                      </ListItemIcon>
                      <ListItemText primary={t(item.translationKey)} />
                    </ListItem>
                  );
                })}
              </>
            )}

            {role === "SalesRep" && (
              <>
                <Typography sx={sectionLabelSx}>
                  {t("sidebar.salesRepPortal")}
                </Typography>

                {salesRepNavItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <ListItem
                      key={item.path}
                      sx={getNavItemStyles(isActive)}
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemIcon>
                        <IconComponent sx={getIconStyles(isActive)} />
                      </ListItemIcon>
                      <ListItemText primary={t(item.translationKey)} />
                    </ListItem>
                  );
                })}
              </>
            )}

            {role === "SalesManager" && (
              <>
                <Typography sx={sectionLabelSx}>
                  {t("sidebar.salesManagerPortal")}
                </Typography>

                {salesManagerNavItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <ListItem
                      key={item.path}
                      sx={getNavItemStyles(isActive)}
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemIcon>
                        <IconComponent sx={getIconStyles(isActive)} />
                      </ListItemIcon>
                      <ListItemText primary={t(item.translationKey)} />
                    </ListItem>
                  );
                })}
              </>
            )}
          </List>
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mx: 2,
            mb: 2,
            p: 1.5,
            borderRadius: "16px",
            bgcolor: NJIM.muted,
          }}
        >
          <Tooltip title={sidebarDisplayName}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: NJIM.inkSoft,
                flexShrink: 0,
              }}
            >
              <img src={UserIcon} alt="User" />
            </Avatar>
          </Tooltip>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: NJIM.textPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {sidebarDisplayName}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary }}>
              {role === "Supervisor" ? t("sidebar.supervisor") : role === "Admin" ? t("sidebar.admin") : role === "WHM" ? t("sidebar.whm") : role === "SalesRep" ? t("sidebar.salesRep") : role === "SalesManager" ? t("sidebar.salesManager") : t("sidebar.b2bCustomer")}
            </Typography>
          </Box>

          <ExitToAppIcon
            role="button"
            tabIndex={0}
            aria-label={t("sidebar.logout")}
            sx={{
              color: NJIM.textSecondary,
              fontSize: 20,
              cursor: "pointer",
              flexShrink: 0,
              transition: "color 180ms ease",
              "&:hover": { color: NJIM.ink },
            }}
            onClick={handleLogout}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleLogout();
            }}
          />
        </Box>

        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 140,
            pointerEvents: "none",
            overflow: "hidden",
            zIndex: 0,
          }}
        >
          <Box
            component="img"
            src={loginLeafMark}
            alt=""
            sx={{
              position: "absolute",
              bottom: -75,
              insetInlineEnd: -40,
              width: 220,
              height: 220,
              objectFit: "contain",
              opacity: 0.1,
              filter: "grayscale(1) sepia(0.15) hue-rotate(95deg)",
              transform: "rotate(-12deg)",
              userSelect: "none",
            }}
          />
        </Box>
      </Drawer>
    </Box>
  );
};

export default Sidebar;
